"use client"

import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {RotateCcw, TriangleAlert} from "lucide-react";
import {recaptchaVerificationAPI} from "@/app/apiPaths";
import {RecaptchaConfigResponse} from "@/pb/api/devplan/services/web/auth";
import type {RecaptchaAction} from "@/app/auth/recaptcha";

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";
const RECAPTCHA_LOAD_CALLBACK = "devplanRecaptchaLoaded";
const RECAPTCHA_LOAD_TIMEOUT_MS = 10_000;
const RECAPTCHA_EMERGENCY_BYPASS_TOKEN = "emergency-bypass";

interface GoogleRecaptchaEnterpriseApi {
  ready(callback: () => void): void;
  execute(siteKey: string, options: {action: RecaptchaAction}): Promise<string>;
}

interface RecaptchaInitialization {
  api: GoogleRecaptchaEnterpriseApi | undefined;
  config: RecaptchaConfigResponse;
}

declare global {
  interface Window {
    grecaptcha?: {enterprise?: GoogleRecaptchaEnterpriseApi};
    devplanRecaptchaLoaded?: () => void;
  }
}

let scriptLoadPromise: Promise<GoogleRecaptchaEnterpriseApi> | undefined;

export interface RecaptchaChallengeHandle {
  requireToken(action: RecaptchaAction): Promise<string | undefined>;
  reset(message?: string): void;
}

interface RecaptchaChallengeProps {
  onReadyChange?(ready: boolean): void;
}

export const RecaptchaChallenge = forwardRef<RecaptchaChallengeHandle, RecaptchaChallengeProps>(function RecaptchaChallenge(
  {onReadyChange},
  ref,
) {
  const apiRef = useRef<GoogleRecaptchaEnterpriseApi | undefined>(undefined);
  const configRef = useRef<RecaptchaConfigResponse | undefined>(undefined);
  const initializationRef = useRef<Promise<RecaptchaInitialization> | undefined>(undefined);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [message, setMessage] = useState<string>();

  useImperativeHandle(ref, () => ({
    async requireToken(action) {
      let api = apiRef.current;
      let config = configRef.current;
      if (!config) {
        try {
          const pending = initializationRef.current ?? initializeRecaptcha();
          initializationRef.current = pending;
          const initialization = await pending;
          api = initialization?.api;
          config = initialization?.config;
        } catch {
          setMessage("reCAPTCHA could not load. Try again before continuing.");
          return undefined;
        }
      }
      if (config?.emergencyBypass) return RECAPTCHA_EMERGENCY_BYPASS_TOKEN;
      if (!api || !config?.siteKey) {
        setMessage("reCAPTCHA could not load. Try again before continuing.");
        return undefined;
      }
      try {
        setMessage(undefined);
        const token = await api.execute(config.siteKey, {action});
        if (!token) throw new Error("reCAPTCHA returned an empty token");
        return token;
      } catch {
        setMessage("reCAPTCHA could not verify you. Try again.");
        return undefined;
      }
    },
    reset(nextMessage) {
      setMessage(nextMessage);
    },
  }), []);

  useEffect(() => {
    let cancelled = false;
    onReadyChange?.(false);
    setMessage(undefined);
    apiRef.current = undefined;
    configRef.current = undefined;

    const initialization = initializationRef.current ?? initializeRecaptcha();
    initializationRef.current = initialization;

    void initialization.then(({api, config}) => {
      if (cancelled) return;
      apiRef.current = api;
      configRef.current = config;
      onReadyChange?.(true);
    }).catch(() => {
      if (cancelled) return;
      onReadyChange?.(false);
      setMessage("reCAPTCHA could not load. Try again, or refresh the page if the problem continues.");
    });

    return () => {
      cancelled = true;
      if (initializationRef.current === initialization) initializationRef.current = undefined;
    };
  }, [loadAttempt, onReadyChange]);

  const retry = () => {
    scriptLoadPromise = undefined;
    initializationRef.current = undefined;
    onReadyChange?.(false);
    document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
    setLoadAttempt(attempt => attempt + 1);
  };

  return <div className="space-y-3" aria-label="reCAPTCHA verification">
    {message && <Alert variant="destructive">
      <TriangleAlert className="h-4 w-4"/>
      <AlertTitle>Verification required</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{message}</p>
        <Button type="button" variant="outline" size="sm" onClick={retry}>
          <RotateCcw className="size-4"/> Try again
        </Button>
      </AlertDescription>
    </Alert>}
  </div>;
});

async function loadRecaptchaConfig(): Promise<RecaptchaConfigResponse> {
  const response = await fetch(recaptchaVerificationAPI(), {cache: "no-store"});
  if (!response.ok) throw new Error("reCAPTCHA configuration unavailable");
  return RecaptchaConfigResponse.fromJSON(await response.json());
}

async function initializeRecaptcha(): Promise<RecaptchaInitialization> {
  const config = await loadRecaptchaConfig();
  return {
    api: config.emergencyBypass ? undefined : await loadRecaptchaScript(config.siteKey),
    config,
  };
}

function loadRecaptchaScript(siteKey: string): Promise<GoogleRecaptchaEnterpriseApi> {
  const existingApi = window.grecaptcha?.enterprise;
  if (existingApi) return recaptchaReady(existingApi);
  if (scriptLoadPromise) return scriptLoadPromise;

  const loadingPromise = new Promise<GoogleRecaptchaEnterpriseApi>((resolve, reject) => {
    document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
    const timeout = window.setTimeout(
      () => reject(new Error("reCAPTCHA initialization timed out")),
      RECAPTCHA_LOAD_TIMEOUT_MS,
    );
    window.devplanRecaptchaLoaded = () => {
      window.clearTimeout(timeout);
      delete window.devplanRecaptchaLoaded;
      const api = window.grecaptcha?.enterprise;
      if (!api) {
        reject(new Error("reCAPTCHA did not initialize"));
        return;
      }
      void recaptchaReady(api).then(resolve);
    };
    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?onload=${RECAPTCHA_LOAD_CALLBACK}&render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      window.clearTimeout(timeout);
      delete window.devplanRecaptchaLoaded;
      reject(new Error("reCAPTCHA script failed to load"));
    };
    document.head.appendChild(script);
  }).catch(err => {
    scriptLoadPromise = undefined;
    delete window.devplanRecaptchaLoaded;
    document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
    throw err;
  });

  scriptLoadPromise = loadingPromise;
  return loadingPromise;
}

function recaptchaReady(api: GoogleRecaptchaEnterpriseApi): Promise<GoogleRecaptchaEnterpriseApi> {
  return new Promise(resolve => api.ready(() => resolve(api)));
}
